import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminFlagsQueryKeys, adminShowFlag, adminUpdateFlag } from 'services/api/admin/flags';
import { adminIndexUsers } from 'services/api/admin/users';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
// Two users can share a name, so the email and client tell them apart.
const getUserLabel = (u: any) => [u.name, u.email, u.client?.name].filter(Boolean).join(' · ');

export default function EditFeatureFlag() {
  const { name } = useParams<{ name: string }>();
  const [searchParams] = useSearchParams();
  const listPath = `/admin/feature-flags?tab=${searchParams.get('tab') === 'global' ? 'global' : 'client'}`;
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminFeatureFlagEdit);

  const { values, dirty, setValue, submit, loading, goBack } = useAdminForm({
    id: name,
    show: adminShowFlag,
    create: async () => {},
    update: async (updateId: string, payload: any) => {
      return adminUpdateFlag(updateId, { name: updateId, ...payload });
    },
    include: [] as const,
    queryKey: adminFlagsQueryKeys,
    onUpdateSuccess: { toast: 'Feature flag successfully updated', navigate: listPath },
    onError: { toast: 'Something went wrong. Please try again.' },
    toFormValues: (data) => ({
      title: data?.title ?? '',
      globallyEnabled: data?.globallyEnabled ?? false,
      category: data?.category ?? null,
      clients: data?.clients ?? [],
      // An API without the users list yet would ignore userIds, so the section stays hidden until it has one.
      hasUsers: Array.isArray(data?.users),
      users: data?.users ?? [],
    }),
    toPayload: (v) => ({
      ...(v.category === 'client' ? { clientIds: v.clients.map((c) => c.id) } : {}),
      ...(v.hasUsers ? { userIds: v.users.map((u) => u.id) } : {}),
    }),
  });

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'featureFlags'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc' },
  });

  const userOpts = useInfiniteOptions({
    queryKey: ['adminUsers', 'featureFlags'],
    // The search also matches similar text, so a full email matches every address at that domain.
    // Ranking by closest match while searching keeps the right person on top, like Admin → Users.
    fetchFn: (params, signal) =>
      adminIndexUsers({ ...params, sortBy: params.search ? 'search desc' : 'userName asc' }, signal, ['client']),
    params: { active: 'true' },
  });

  if (loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title={canEdit ? 'Edit Feature Flag' : 'Feature Flag'} subtitle={values?.title} />

      {values?.category === 'client' && (
        <InputGroup title={`Clients - ${values.clients.length} selected`}>
          {canEdit ? (
            <MultiSelectChips
              {...clientOpts}
              values={values.clients}
              onChange={(v) => setValue('clients', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select...'
            />
          ) : (
            <ReadOnlyList items={values.clients.map(getName)} />
          )}
        </InputGroup>
      )}

      {values?.hasUsers && (
        <InputGroup title={`Users - ${values.users.length} selected`}>
          {values.globallyEnabled && (
            <Note>This flag is on for everyone. Turn it off for everyone to target specific users.</Note>
          )}
          {/* Users on a flag that's on for everyone change nothing, and Disable for Everyone removes them. */}
          {canEdit && !values.globallyEnabled ? (
            <MultiSelectChips
              {...userOpts}
              values={values.users}
              onChange={(v) => setValue('users', v)}
              getOptionValue={getId}
              getOptionLabel={getUserLabel}
              placeholder='Search by name or email...'
            />
          ) : (
            <ReadOnlyList items={values.users.map(getUserLabel)} />
          )}
        </InputGroup>
      )}

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={goBack}>
          {canEdit ? 'Cancel' : 'Back'}
        </Button>
        {canEdit && (
          <Button onClick={submit} disabled={!dirty}>
            Update
          </Button>
        )}
      </ButtonGroup>
    </AdminFormPage>
  );
}

// A disabled picker still lets a chip's x be clicked, so view-only admins get plain text instead.
function ReadOnlyList({ items }: { items: string[] }) {
  if (items.length === 0) return <BodySmall>None</BodySmall>;
  return (
    <List>
      {items.map((item) => (
        <BodySmall key={item}>{item}</BodySmall>
      ))}
    </List>
  );
}

const Note = styled(BodySmall)`
  color: ${colors.black75};
  margin-bottom: 8px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
